/**
 * Signup — creates a new tenant. Supabase signUp() emails a verification
 * link if email confirmation is enabled in the project settings. On
 * successful verification, the first authenticated request triggers
 * ensureAccount() in apps/api which provisions the workspace.
 *
 * Workspace name is captured here and passed as user_metadata so the
 * server can rename the workspace on first call (otherwise it defaults
 * to "{localpart}'s Workspace").
 *
 * The user's name goes in as user_metadata.full_name; handle_new_user copies
 * it to public.users.full_name, whose first word signs AI-drafted emails.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import { friendlyError } from '@/lib/utils/friendly-errors';
import { AuthLayout } from '@/components/layout/AuthLayout';
import { CookieBanner } from '@/components/layout/CookieBanner';
import { PasswordInput } from '@/components/ui/PasswordInput';
import { readProductHeader } from '@/lib/product';
import { trialTerms } from '@/lib/billing';

const PWD_MIN = 10;

/** Headline per product front door (lib/product.ts). null = the default
 *  business workspace. SOW Desk keeps the generic copy (invite-only). */
const PRODUCT_HEADLINE: Record<string, { title: string; sub: string }> = {
  sales:   { title: 'Start your Waypoint Sales trial',   sub: `Create your account, then start your trial. ${trialTerms('sales')}` },
  clients: { title: 'Start your Waypoint Clients trial', sub: 'A Clients workspace for your team — 14 days, no credit card required.' },
};

/* ── SSO icon SVGs ─────────────────────────────────────────────── */

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59a14.5 14.5 0 0 1 0-9.18l-7.98-6.19a24.01 24.01 0 0 0 0 21.56l7.98-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

const MicrosoftIcon = () => (
  <svg width="18" height="18" viewBox="0 0 21 21">
    <rect x="1" y="1" width="9" height="9" fill="#F25022" />
    <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
    <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
    <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
  </svg>
);

const SSOSpinner = () => (
  <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
);

export function Signup() {
  useEffect(() => { document.title = 'Create your workspace — Waypoint'; }, []);
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Preserve a deep-link destination (e.g. a ?trial=1 checkout link) through the
  // whole signup flow — SSO, email-confirm, and immediate-session paths.
  const nextParam = searchParams.get('next');
  const nextTarget = nextParam ?? '/dashboard';

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [workspaceName, setWorkspaceName] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmSent, setConfirmSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ssoLoading, setSsoLoading] = useState<'google' | 'microsoft' | null>(null);

  useEffect(() => {
    if (!loading && user) navigate(nextTarget, { replace: true });
  }, [user, loading, navigate]);

  function validate(): string | null {
    if (!fullName.trim()) return 'Please enter your name.';
    if (password.length < PWD_MIN) return `Password must be at least ${PWD_MIN} characters.`;
    if (password !== confirm) return 'Passwords do not match.';
    if (!/[A-Z]/.test(password) || !/[a-z]/.test(password) || !/\d/.test(password)) {
      return 'Password must include upper-case, lower-case, and a number.';
    }
    if (!agreedToTerms) return 'You must agree to the Terms and Privacy Policy.';
    return null;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const v = validate();
    if (v) { setError(v); return; }
    setSubmitting(true);
    try {
      const { data, error: signUpError } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback?next=${encodeURIComponent(nextTarget)}`,
          data: {
            full_name: fullName.trim(),
            workspace_name: workspaceName.trim() || null,
          },
        },
      });
      if (signUpError) throw signUpError;

      // If Supabase project has email_confirm=true (recommended for prod),
      // session will be null and the user must click email link first.
      // If email_confirm=false, the session is established immediately.
      if (!data.session) {
        setConfirmSent(true);
      } else {
        navigate(nextTarget, { replace: true });
      }
    } catch (err) {
      const msg = friendlyError(err);
      // Common Supabase errors → friendlier copy
      setError(
        /already registered/i.test(msg) ? 'That email is already registered. Try signing in instead.'
        : /weak/i.test(msg) ? 'Password too weak. Use a longer, more unique passphrase.'
        : msg
      );
    } finally {
      setSubmitting(false);
    }
  }

  const handleGoogleSSO = async () => {
    setSsoLoading('google');
    setError(null);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/auth/callback?next=${encodeURIComponent(nextTarget)}` },
    });
    if (error) { setError('Google sign-in failed. Please try again.'); setSsoLoading(null); }
  };

  const handleMicrosoftSSO = async () => {
    setSsoLoading('microsoft');
    setError(null);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'azure',
      // Entra only returns the user's address with the `email` scope and their
      // name with `profile` (see Login.tsx). Same fix on both entry points.
      options: {
        scopes: 'email profile',
        redirectTo: `${window.location.origin}/auth/callback?next=${encodeURIComponent(nextTarget)}`,
      },
    });
    if (error) { setError('Microsoft sign-in failed. Please try again.'); setSsoLoading(null); }
  };

  return (
    <AuthLayout>
      <div className="rounded-xl border border-border bg-surface-1 p-8 shadow-glow">
        {confirmSent ? (
          <div className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-success-soft text-2xl">&#9993;</div>
            <h1 className="mb-2 text-xl font-medium text-ink">Verify your email</h1>
            <p className="text-sm text-ink-muted">
              We sent a verification link to <span className="font-mono text-primary-bright">{email}</span>. Click it to finish setting up your account.
            </p>
            <p className="mt-4 text-xs text-ink-dim">
              Didn't get it? Check spam, or <button onClick={() => setConfirmSent(false)} className="text-primary-bright hover:underline">try again</button>.
            </p>
          </div>
        ) : (
          <>
            <h1 className="mb-1 text-xl font-medium text-ink">{PRODUCT_HEADLINE[readProductHeader() ?? '']?.title ?? 'Create your workspace'}</h1>
            <p className="mb-5 text-sm text-ink-muted">
              {PRODUCT_HEADLINE[readProductHeader() ?? '']?.sub ?? `Create your account, then pick a plan. ${trialTerms()}`}
            </p>

            {/* SSO buttons */}
            <div className="space-y-3">
              <button
                type="button"
                onClick={handleGoogleSSO}
                disabled={ssoLoading !== null}
                className="w-full flex items-center justify-center gap-3 rounded-xl border border-border bg-surface-1 px-4 py-3 text-sm font-medium text-ink hover:bg-surface-2 transition-colors disabled:opacity-60"
              >
                {ssoLoading === 'google' ? <SSOSpinner /> : <GoogleIcon />}
                <span>Continue with Google</span>
              </button>
              <button
                type="button"
                onClick={handleMicrosoftSSO}
                disabled={ssoLoading !== null}
                className="w-full flex items-center justify-center gap-3 rounded-xl border border-border bg-surface-1 px-4 py-3 text-sm font-medium text-ink hover:bg-surface-2 transition-colors disabled:opacity-60"
              >
                {ssoLoading === 'microsoft' ? <SSOSpinner /> : <MicrosoftIcon />}
                <span>Continue with Microsoft</span>
              </button>
            </div>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
              <div className="relative flex justify-center text-xs"><span className="bg-surface-1 px-3 text-ink-muted">or continue with email</span></div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="mb-1.5 block text-xs font-medium text-ink-dim" htmlFor="full-name">
                  Your name
                </label>
                <input
                  id="full-name"
                  type="text"
                  required
                  autoComplete="name"
                  maxLength={80}
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Jordan Lee"
                  className="w-full rounded-md border border-border bg-surface-2 px-3 py-2 text-sm text-ink outline-hidden focus:border-primary-line"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-ink-dim" htmlFor="ws">
                  Workspace name <span className="text-ink-dim/60">(optional)</span>
                </label>
                <input
                  id="ws"
                  type="text"
                  autoComplete="organization"
                  maxLength={80}
                  value={workspaceName}
                  onChange={(e) => setWorkspaceName(e.target.value)}
                  placeholder="Acme Advisory Group"
                  className="w-full rounded-md border border-border bg-surface-2 px-3 py-2 text-sm text-ink outline-hidden focus:border-primary-line"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-ink-dim" htmlFor="email">
                  Email address
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="w-full rounded-md border border-border bg-surface-2 px-3 py-2 text-sm text-ink outline-hidden focus:border-primary-line"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-ink-dim" htmlFor="password">
                  Password
                </label>
                <PasswordInput
                  id="password"
                  required
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  minLength={PWD_MIN}
                />
                <p className="mt-1 text-2xs text-ink-dim">
                  {PWD_MIN}+ characters, mix of upper, lower, and a number.
                </p>
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-ink-dim" htmlFor="confirm">
                  Confirm password
                </label>
                <PasswordInput
                  id="confirm"
                  required
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                />
              </div>

              <label className="flex items-start gap-2 text-xs text-ink-muted">
                <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded-sm border-border accent-primary"
                />
                <span>
                  I agree to the <a href="/terms" className="text-primary-bright hover:underline">Terms</a> and <a href="/privacy" className="text-primary-bright hover:underline">Privacy Policy</a>.
                </span>
              </label>

              {error && (
                <div className="rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-sm text-danger">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="btn btn-primary w-full py-2.5"
              >
                {submitting ? 'Creating account...' : 'Create account'}
              </button>
            </form>

            <p className="mt-6 text-center text-sm text-ink-muted">
              Already have an account? <Link to={nextParam ? `/login?next=${encodeURIComponent(nextParam)}` : '/login'} className="text-primary-bright hover:underline font-medium">Sign in</Link>
            </p>
          </>
        )}
      </div>
      {/* The auth pages sit OUTSIDE MarketingLayout, so they were the one
          public surface with no consent banner — a visitor could land
          straight on /signup from an ad and never be asked. */}
      <CookieBanner />
    </AuthLayout>
  );
}
